// clientWrite — resilient writes to public.clients.
//
// The clients table's columns drift per tenant (some accounts lack
// security_code / billing_schedule_mode / part_a_effective / agent_notes / …).
// A single missing column makes PostgREST 500 the whole insert/update, which
// showed up as "won't let me add/save a client." These helpers retry-with-drop:
// on a "Could not find the 'X' column" error they remove X and retry, so the
// write succeeds with whatever columns the tenant actually has. Dropped columns
// are returned (log them), never fatal. Any OTHER error is returned as-is.
//
// Handles a single row object OR an array of rows (bulk insert).

import { supabase } from './supabase';

const MISSING_COL = /could not find the '([a-z0-9_]+)' column/i;

export async function insertClientSafe(rowOrRows, selectCols = null) {
  const isArr = Array.isArray(rowOrRows);
  let payload = isArr ? rowOrRows.map((r) => ({ ...r })) : { ...rowOrRows };
  const has = (c) => (isArr ? payload.some((r) => Object.prototype.hasOwnProperty.call(r, c)) : Object.prototype.hasOwnProperty.call(payload, c));
  const drop = (c) => { if (isArr) payload.forEach((r) => { delete r[c]; }); else delete payload[c]; };
  const dropped = [];
  for (let i = 0; i < 30; i++) {
    let q = supabase.from('clients').insert(payload);
    if (selectCols) { q = q.select(selectCols); if (!isArr) q = q.single(); }
    const { data, error } = await q;
    if (!error) return { data, dropped };
    const m = MISSING_COL.exec(error.message || '');
    if (m && has(m[1])) { drop(m[1]); dropped.push(m[1]); continue; }
    return { error, dropped };
  }
  return { error: { message: 'clients insert failed: too many unknown columns' }, dropped };
}

export async function updateClientSafe(patch, id) {
  let payload = { ...patch };
  const dropped = [];
  for (let i = 0; i < 30; i++) {
    if (Object.keys(payload).length === 0) return { dropped, noop: true }; // all cols dropped — nothing to write
    const { error } = await supabase.from('clients').update(payload).eq('id', id);
    if (!error) return { dropped };
    const m = MISSING_COL.exec(error.message || '');
    if (m && Object.prototype.hasOwnProperty.call(payload, m[1])) { delete payload[m[1]]; dropped.push(m[1]); continue; }
    return { error, dropped };
  }
  return { error: { message: 'clients update failed: too many unknown columns' }, dropped };
}
